import React from "react";
import { Card, CardTitle, CardHeader, CardContent } from "../ui/card";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useCitation } from "./CitationContext";
import { formatPersonDateForDisplay } from "@/shared/utils/formatters";

// Simple reusable card for document insights
export function InsightsCard({
  title,
  subtitle,
  children,
  noPadding,
  headerVariant,
  headerContent,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  noPadding?: boolean;
  headerVariant?: "default" | "alert";
  headerContent?: React.ReactNode;
}) {
  const headerBg = headerVariant === "alert" ? "bg-red-50" : "bg-stone-50";
  const titleColor = headerVariant === "alert" ? "text-red-700" : "text-stone-700";

  return (
    <Card className="w-full border border-stone-200">
      <CardHeader className={`p-4 border-b-0 border-stone-200 ${headerBg} rounded-t-xl`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className={`text-base font-medium ${titleColor}`}>{title}</CardTitle>
            {subtitle && <p className="text-sm text-stone-500 mt-0.5">{subtitle}</p>}
          </div>
          {headerContent}
        </div>
      </CardHeader>
      <CardContent className={noPadding ? "p-0" : "space-y-4 p-4"}>{children}</CardContent>
    </Card>
  );
}

// Divider list wrapper - adds dividers between children
export function InsightsDividerList({ children }: { children: React.ReactNode }) {
  return <div className="divide-y divide-stone-100">{children}</div>;
}

// Divider row - used within InsightsDividerList
export function InsightsDividerRow({
  label,
  value,
  labelClassName,
  layout = "grid",
  onClick,
}: {
  label: string;
  value: React.ReactNode;
  labelClassName?: string;
  layout?: "grid" | "between";
  onClick?: () => void;
}) {
  return (
    <div
      className={`${
        layout === "between"
          ? "flex justify-between items-start py-2.5 px-4"
          : "grid grid-cols-2 gap-4 py-2.5 px-4"
      }${onClick ? " cursor-pointer hover:bg-subtle transition-colors rounded-sm" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter") onClick();
            }
          : undefined
      }
    >
      <span className={`text-sm text-secondary inline-flex items-center ${labelClassName || ""}`}>
        {label}
        {onClick && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />}
      </span>
      <span className={`text-sm text-primary ${layout === "between" ? "text-right" : ""}`}>
        {value || <span className="text-stone-400">Not specified</span>}
      </span>
    </div>
  );
}

// Row component for label-value pairs (non-divider style)
export function InsightsRow({
  label,
  value,
  className,
}: {
  label: string;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex flex-col md:flex-row md:items-center ${className || ""}`}>
      <span className="text-sm font-light text-secondary md:w-[200px] md:flex-shrink-0 mb-2 md:mb-0">
        {label}
      </span>
      <div className="flex-1 md:ml-[64px]">
        <span className="text-sm">
          {value || <span className="text-stone-400">Not specified</span>}
        </span>
      </div>
    </div>
  );
}

// Credit score visualization bar
export function CreditScoreBar({ score }: { score: number }) {
  const getScorePosition = (s: number): number => {
    const min = 300;
    const max = 850;
    const position = ((s - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  const getScoreLabel = (s: number): string => {
    if (s >= 800) return "Exceptional";
    if (s >= 740) return "Very Good";
    if (s >= 670) return "Good";
    if (s >= 580) return "Fair";
    return "Poor";
  };

  const position = getScorePosition(score);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-6">
        <div className="shrink-0 w-16">
          <p className="text-4xl font-bold text-primary">{score}</p>
        </div>
        <div className="flex-1">
          <div className="flex justify-between text-sm text-stone-600 mb-1">
            <span>Poor</span>
            <span>Fair</span>
            <span>Good</span>
            <span>Very Good</span>
            <span>Exceptional</span>
          </div>
          <div
            className="h-3 rounded-full relative"
            style={{
              background: "linear-gradient(to right, #FF6568, #FEB837, #F8F449, #A2C74C, #07CE94)",
            }}
          >
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-stone-300 rounded-full shadow"
              style={{ left: `calc(${position}% - 8px)` }}
            />
          </div>
        </div>
      </div>
      <p className="text-sm text-stone-600">{getScoreLabel(score)}</p>
    </div>
  );
}

// Eye toggle shared by the masked value components
function MaskToggleButton({ isVisible, onToggle }: { isVisible: boolean; onToggle: () => void }) {
  const { clearCitation } = useCitation();

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        if (isVisible) {
          clearCitation();
        }
        onToggle();
      }}
      className="text-stone-400 hover:text-stone-600 transition-colors"
      aria-label={isVisible ? "Hide value" : "Show value"}
    >
      {isVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
    </button>
  );
}

// Masked value component (for SSN, EIN, etc.)
export function MaskedValue({
  value,
  format = "ssn",
  maskChar = "X",
}: {
  value: string | null | undefined;
  format?: "ssn" | "ein";
  maskChar?: string;
}) {
  const [isVisible, setIsVisible] = useState(false);

  if (!value) {
    return <span className="text-stone-400">Not specified</span>;
  }

  const maskedValue =
    format === "ein"
      ? `${maskChar.repeat(2)}-${maskChar.repeat(7)}`
      : `${maskChar.repeat(3)}-${maskChar.repeat(2)}-${maskChar.repeat(4)}`;

  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono">{isVisible ? value : maskedValue}</span>
      <MaskToggleButton isVisible={isVisible} onToggle={() => setIsVisible(!isVisible)} />
    </span>
  );
}

// Masked account number component (shows X's with dashes matching the format)
export function MaskedAccountNumber({
  value,
  isLoading,
}: {
  value: string | null | undefined;
  isLoading?: boolean;
}) {
  const [isVisible, setIsVisible] = useState(false);

  if (isLoading) {
    return <span className="text-stone-400">Loading...</span>;
  }

  if (!value) {
    return <span className="text-stone-400">-</span>;
  }

  // Format account number with dashes (groups from right: 3-4-remainder)
  const formatAccountNumber = (num: string): string => {
    const digitsOnly = num.replace(/\D/g, "");
    const len = digitsOnly.length;

    if (len <= 3) return digitsOnly;
    if (len <= 7) return `${digitsOnly.slice(0, -3)}-${digitsOnly.slice(-3)}`;
    // For 8+ digits: remainder-4-3 pattern
    return `${digitsOnly.slice(0, -7)}-${digitsOnly.slice(-7, -3)}-${digitsOnly.slice(-3)}`;
  };

  // Create masked version: same grouping but with all X's
  const createMaskedValue = (num: string): string => {
    const digitsOnly = num.replace(/\D/g, "");
    const len = digitsOnly.length;
    const allX = "X".repeat(len);

    if (len <= 3) return allX;
    if (len <= 7) return `${allX.slice(0, -3)}-${allX.slice(-3)}`;
    return `${allX.slice(0, -7)}-${allX.slice(-7, -3)}-${allX.slice(-3)}`;
  };

  const displayValue = isVisible ? formatAccountNumber(value) : createMaskedValue(value);

  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-mono">{displayValue}</span>
      <MaskToggleButton isVisible={isVisible} onToggle={() => setIsVisible(!isVisible)} />
    </span>
  );
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

const SHORT_MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return "";
  const parts = dateString.split("-");
  if (parts.length === 1 && parts[0].length === 4) {
    return parts[0];
  } else if (parts.length === 2) {
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) return dateString;
    return `${MONTH_NAMES[monthIndex]} ${parts[0]}`;
  } else if (parts.length === 3) {
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) return dateString;
    return formatPersonDateForDisplay(dateString, undefined, dateString);
  }
  return dateString;
}

// Utility: format role names (snake_case to Title Case)
export function formatRoleName(role: string | null | undefined): string {
  if (!role) return "";
  return role.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// Utility: format currency
export function formatCurrency(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatMonthYear(dateString: string | null | undefined): string {
  if (!dateString) return "";
  const parts = dateString.split("-");
  if (parts.length >= 2) {
    const monthIndex = parseInt(parts[1], 10) - 1;
    if (monthIndex < 0 || monthIndex > 11) return dateString;
    return `${SHORT_MONTH_NAMES[monthIndex]} ${parts[0]}`;
  }
  return dateString;
}

// List item for accounts/items with title, subtitle, badge, and right content
export function InsightsListItem({
  title,
  subtitle,
  badge,
  rightContent,
  onClick,
}: {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  rightContent?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className={`flex items-start justify-between py-3 px-4 border-b border-stone-100 last:border-0${onClick ? " cursor-pointer hover:bg-subtle transition-colors" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onClick();
            }
          : undefined
      }
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-primary truncate inline-flex items-center">
            {title}
            {onClick && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-300 shrink-0" />}
          </p>
          {badge}
        </div>
        {subtitle && <p className="text-sm text-stone-500 mt-0.5">{subtitle}</p>}
      </div>
      {rightContent && <div className="ml-4 shrink-0">{rightContent}</div>}
    </div>
  );
}

// Empty state message
export function InsightsEmptyState({ message }: { message: string }) {
  return <p className="text-sm text-stone-500 text-center py-2">{message}</p>;
}

// Large stat display
export function InsightsStat({
  label,
  value,
  size = "default",
}: {
  label: string;
  value: string;
  size?: "default" | "large";
}) {
  return (
    <div className="text-center">
      <p className="text-sm text-secondary">{label}</p>
      <p className={`font-semibold text-primary ${size === "large" ? "text-2xl" : "text-base"}`}>
        {value}
      </p>
    </div>
  );
}
